/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderNode from "App/render/webgl/nodes/render_node";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import RenderablesFactory from "App/render/webgl/renderables/factory";
import RenderObject from "App/geometry/objects/object";
import ModelStatistics from "App/model_statistics";
import ImageDataLoader from "App/utils/textures/image_data_loader";
import Pass from "App/render/webgl/passes/pass";
import {ERenderableFilters} from "App/render/webgl/render_state";
import Factory from "App/render/webgl/nodes/factory";
import Texture from "App/render/webgl/renderables/texture";
import {TextureNodeTypeName} from "App/render/webgl/nodes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class TextureNode extends RenderNode {

    public static TypeName: string = TextureNodeTypeName;
    private renderable: Texture;
    private _switching: any;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        super(aSceneAPI, aRenderGraph, aParentNode, aShape);

        this._typeName = TextureNode.TypeName;

        this.renderable = RenderablesFactory.create(Texture, aSceneAPI, aRenderGraph, this) as Texture;
        if (this.appearance.texture) {
            this.renderable.setProperties(this.appearance.texture.get());
        }

        this._switching = null;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        return new TextureNode(aSceneAPI, aRenderGraph, aParentNode, aShape);
    }

    public isSwitching(): boolean {
        return this._switching !== null;
    }

    public getRenderable(): Texture {
        return this.renderable;
    }

    public removed() {
        this.release();
    }

    public release() {
        super.release();

        if (this.renderable) {
            this.renderable.release();
        }
    }

    public compareProperties(aShape: RenderObject): boolean {
        return this.appearance.texture === aShape.appearance.texture;
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        if (this.appearance.hasTexture(null)) {
            aStatistics.addTexture(this.appearance.texture.get().url.length > 1);
        }

        aStatistics.addTextureNode();

        super.countShapesAndUniqueTextures(aStatistics);
    }

    /***
     * Called when we need to create new renderable with the data stored in the aImageData
     * TODO: upload the data from the aImageData tile by tile to have smooth exploration of the scene wen moving
     * @param aImageData
     */
    public setTextureData(aImageData: ImageDataLoader) {
        if (this._switching && this._switching.imageData.getLevel() !== aImageData.getLevel()) {
            this._switching.imageData.removeObserver();
            this._switching.renderable.release();
            this._switching = null;
        }

        if (!this._switching) {
            this._switching = {
                iterator: 0,
                imageData: aImageData,
                renderable: RenderablesFactory.create(Texture, this._sceneAPI, this._renderGraph, this)
            };
            this._switching.imageData.addObserver();
            this._switching.renderable.setProperties(this.appearance.texture.get());
        }
    }

    public render(aRenderPass: Pass): boolean {
        if (this._switching) {
            this._iterateSwitching();
        }

        let hasTextureFilter = aRenderPass.state.getRenderableFilterIsSet(ERenderableFilters.texture);
        this.renderable.render(aRenderPass, !hasTextureFilter);

        // pass the render call to children
        this._renderReturnVal = super.render(aRenderPass);

        if (hasTextureFilter) {
            this.renderable.finishRender(aRenderPass);
        }

        return this._renderReturnVal;
    }

    private _iterateSwitching() {
        let tile = this._switching.imageData.tiles[this._switching.iterator];
        let cols = this._switching.imageData.getCols();
        let rows = this._switching.imageData.getRows();

        let row = Math.floor(this._switching.iterator / cols);
        let col = this._switching.iterator % cols;

        // if iterator is 0, then we create new gl texture
        if (this._switching.iterator === 0) {
            this._switching.renderable.createEmptyTexture(this._switching.imageData.getDiffuseProperties());
        }

        // upload tile to renderable
        this._switching.renderable.uploadTile(this._switching.imageData.getDiffuseProperties(), row, col, tile);

        this._switching.iterator++;

        // all tiles are uploaded
        if (cols * rows === this._switching.iterator) {
            // generate mipmaps but only when no compression is used
            if (this._switching.imageData.getNeedMipmapsGeneration()) {
                this._switching.renderable.generateMipmaps();
            }

            this._switching.imageData.removeObserver();

            if (tile.data) {
                this.renderable.release();
                this.renderable = this._switching.renderable;
            } else {
                // when uploaded data is empty, we jsut keep the old renderable,
                // this is because of canvas texture dictionary texture is filled from the CanvasTextureDictironary class, not from texture node
                // so the empty texture can rewrite already filled texture from the dictionary
                this._switching.renderable.release();
            }
            this._switching = null;
        } else {
            this._sceneAPI.viewModeModule.scheduleRedraw();
        }
    }
}

Factory.register(TextureNode);
